import { getFirestore } from "firebase/firestore";
import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import * as Sentry from "@sentry/browser";

// The domain Google/Microsoft show on their sign-in screen ("Sign in to ...").
// Defaults to Firebase's own domain. Set REACT_APP_FIREBASE_AUTH_DOMAIN to
// app.123wellness.org in the Cloudflare Pages production environment to show
// our domain instead - served by the proxy in functions/__/[[path]].js.
//
// Do NOT set it until https://<that domain>/__/auth/handler is added to the
// authorized redirect URIs of the Google OAuth web client and the Microsoft
// app registration, or Google/Microsoft sign-in breaks for everyone.
const authDomain =
  process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "wellness-a9157.firebaseapp.com";

const firebaseConfig = {
  apiKey: "AIzaSyAMl2P7ekzVYXrA-v_uvLdXmb7s-bRwvd0",
  authDomain,
  projectId: "wellness-a9157",
  storageBucket: "wellness-a9157.appspot.com",
  messagingSenderId: "830130482578",
  appId: "1:830130482578:web:c65840e8fcd403cba9a54b",
  measurementId: "G-32YTP3YDDV",
};
// Ensure we don't reinitialize firebase if it's already initialized
const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
export const authentication = getAuth(app);
export const firestore = getFirestore(app);

// check if storage (locl/session) is usable
function isStorageUsable(storage) {
  try {
    const key = "__persistence_test__";
    storage.setItem(key, "1");
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

// check if IndexedDB is available
async function isIndexedDBAvailable() {
  if (!window.indexedDB) {
    return false;
  }

  try {
    if (typeof indexedDB.databases === "function") {
      await indexedDB.databases();
    } else {
      const req = indexedDB.open("__idb_test__");
      await new Promise((resolve, reject) => {
        req.onsuccess = () => {
          req.result.close();
          indexedDB.deleteDatabase("__idb_test__");
          resolve();
        };
        req.onerror = () => reject(req.error);
      });
    }
    return true;
  } catch {
    return false;
  }
}

async function configurePersistence(auth) {
  const context = {
    userAgent: navigator.userAgent,
    cookiesEnabled: navigator.cookieEnabled,
    localStorageUsable: isStorageUsable(localStorage),
    sessionStorageUsable: isStorageUsable(sessionStorage),
  };

  if (navigator.userAgentData) {
    context.userAgentData = {
      brands: navigator.userAgentData.brands,
      mobile: navigator.userAgentData.mobile,
      platform: navigator.userAgentData.platform,
    };
  }

  try {
    // try local storage first (best persistence)
    await setPersistence(auth, browserLocalPersistence);
    Sentry.addBreadcrumb({
      category: "auth",
      message: "Persistence set to browserLocalPersistence",
      level: "info",
      data: context,
    });
    Sentry.setTag("auth.persistence", "local");
    return "local";
  } catch (errLocal) {
    Sentry.captureException(errLocal, {
      contexts: { persistence: { attempted: "local", ...context } },
      level: "warning",
    });

    try {
      // fallback: session storage
      await setPersistence(auth, browserSessionPersistence);
      Sentry.addBreadcrumb({
        category: "auth",
        message: "Persistence set to browserSessionPersistence",
        level: "info",
        data: context,
      });
      Sentry.setTag("auth.persistence", "session");
      return "session";
    } catch (errSession) {
      Sentry.captureException(errSession, {
        contexts: { persistence: { attempted: "session", ...context } },
        level: "warning",
      });

      try {
        // final fallback: in-memory (non-persistent)
        const idbAvailable = await isIndexedDBAvailable();
        if (!idbAvailable) {
          await setPersistence(auth, inMemoryPersistence);
          Sentry.addBreadcrumb({
            category: "auth",
            message: "Persistence set to inMemoryPersistence",
            level: "info",
            data: context,
          });

          Sentry.setTag("auth.persistence", "in-memory");
          return "in-memory";
        } else {
          throw new Error("IndexedDB available but persistence failed.");
        }
      } catch (errMemory) {
        Sentry.captureException(errMemory, {
          contexts: { persistence: { attempted: "in-memory", ...context } },
          level: "error",
        });
        Sentry.setTag("auth.persistence", "failed");
        throw errMemory;
      }
    }
  }
}

// Instead of running it immediately, store the promise
export const persistenceReady = (async () => {
  try {
    await configurePersistence(authentication);
  } catch (err) {
    Sentry.captureException(err, { level: "error" });
  }
})();

export const Signout = () => {
  return signOut(authentication)
    .then(() => {
      Sentry.setUser(null);
      Sentry.getCurrentScope().clear();
    })
    .catch((error) => {
      Sentry.captureException(error);
    });
};
